import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ScanInput from './ScanInput';
import './QuickShipmentPage.css';

// Import sound effects if they exist
let successSound, errorSound, completeSound;
try {
  successSound = new Audio('/sounds/success.mp3');
  errorSound = new Audio('/sounds/error.mp3');
  completeSound = new Audio('/sounds/complete.mp3');
} catch (e) {
  console.log('Sound files not available');
}

const STATES = {
  SCAN_ITEM: 'SCAN_ITEM',
  SEARCHING: 'SEARCHING',
  ORDERS_FOUND: 'ORDERS_FOUND',
  WEIGHING: 'WEIGHING',
  PROCESSING: 'PROCESSING',
  LABEL_READY: 'LABEL_READY',
  ERROR: 'ERROR'
};

const BOX_SIZES = [
  { id: 'small', name: 'Small Box', dimensions: '8x6x4' },
  { id: 'medium', name: 'Medium Box', dimensions: '12x10x8' },
  { id: 'large', name: 'Large Box', dimensions: '16x14x12' },
  { id: 'envelope', name: 'Envelope', dimensions: 'Flat' }
];

function QuickShipmentPage() {
  const [state, setState] = useState(STATES.SCAN_ITEM);
  const [scannedItem, setScannedItem] = useState('');
  const [orders, setOrders] = useState([]);
  const [weight, setWeight] = useState('');
  const [boxSize, setBoxSize] = useState('small');
  const [labelData, setLabelData] = useState(null);
  const [error, setError] = useState('');
  const [autoWeight, setAutoWeight] = useState(null);

  const scanInputRef = useRef(null);
  const weightInputRef = useRef(null);

  // Auto-focus management
  useEffect(() => {
    if (state === STATES.SCAN_ITEM && scanInputRef.current) {
      scanInputRef.current.focus();
    } else if (state === STATES.WEIGHING && weightInputRef.current && !autoWeight) {
      weightInputRef.current.focus();
    }
  }, [state, autoWeight]);

  // Auto-fetch weight from scale when in WEIGHING state
  useEffect(() => {
    if (state === STATES.WEIGHING && !weight) {
      fetchScaleWeight();
    }
  }, [state]);

  // Auto-reset after label is ready
  useEffect(() => {
    if (state === STATES.LABEL_READY) {
      const timer = setTimeout(() => {
        resetToScan();
      }, 5000); // Auto-reset after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [state]);

  const playSound = (sound) => {
    if (sound) {
      sound.currentTime = 0;
      sound.play().catch(() => {});
    }
  };

  const fetchScaleWeight = async () => {
    try {
      const response = await axios.get('/api/scale/weight');
      if (response.data.weight && response.data.weight > 0) {
        setAutoWeight(response.data.weight);
        setWeight(response.data.weight.toString());
      }
    } catch (err) {
      console.log('Scale not available, manual entry required');
    }
  };

  const handleItemScan = async (itemIdentifier) => {
    setScannedItem(itemIdentifier);
    setState(STATES.SEARCHING);
    setError('');

    try {
      const response = await axios.post('/api/fulfillment/quick-shipment/find-order', {
        itemIdentifier
      });

      if (response.data.found && response.data.orders) {
        setOrders(response.data.orders);
        setState(STATES.ORDERS_FOUND);
        playSound(successSound);
      } else {
        setError(response.data.message || 'No single-item orders found for this item');
        setState(STATES.ERROR);
        playSound(errorSound);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to find order');
      setState(STATES.ERROR);
      playSound(errorSound);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setOrders(orders.map(order =>
      order.id === orderId ? { ...order, selected: !order.selected } : order
    ));
  };

  const handleContinueToWeighing = () => {
    const selectedOrders = orders.filter(o => o.selected);
    if (selectedOrders.length === 0) {
      setError('Please select at least one order');
      return;
    }
    setState(STATES.WEIGHING);
  };

  const handleProcessShipment = async () => {
    if (!weight || parseFloat(weight) <= 0) {
      setError('Please enter a valid weight');
      return;
    }

    const selectedOrders = orders.filter(o => o.selected);
    if (selectedOrders.length === 0) {
      setError('No orders selected');
      return;
    }

    setState(STATES.PROCESSING);
    setError('');

    try {
      // Process all selected orders
      const results = [];
      for (const order of selectedOrders) {
        const response = await axios.post('/api/fulfillment/quick-shipment/process', {
          orderId: order.id,
          orderNumber: order.orderNumber,
          customerId: order.customerId,
          items: order.items,
          weight: parseFloat(weight),
          boxSize: BOX_SIZES.find(b => b.id === boxSize)?.name || 'Medium Box',
          shippingAddress: order.shippingAddress
        });

        if (response.data.success) {
          results.push(response.data);
          // Auto-print labels
          if (response.data.labelData) {
            setTimeout(() => {
              printLabel(response.data.labelData, response.data.trackingNumber);
            }, 500 * results.length);
          }
        }
      }

      if (results.length > 0) {
        setLabelData({ results, count: results.length });
        setState(STATES.LABEL_READY);
        playSound(completeSound);
      } else {
        setError('Failed to process shipments');
        setState(STATES.ERROR);
        playSound(errorSound);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process shipment');
      setState(STATES.ERROR);
      playSound(errorSound);
    }
  };

  const printLabel = async (labelBase64, trackingNumber) => {
    try {
      // Decode base64 to ZPL text
      const zplContent = atob(labelBase64);

      // Try to send to Zebra printer via server
      try {
        const printerIp = localStorage.getItem('zebraPrinterIp') || '192.168.1.100';
        await axios.post('/api/print/zpl', {
          zpl: zplContent,
          printerIp: printerIp
        });
        console.log('Label sent to printer');
      } catch (printErr) {
        console.log('Direct print failed, downloading instead:', printErr.message);
        // Download as .zpl file
        downloadZplFile(zplContent, trackingNumber);
      }
    } catch (err) {
      console.error('Failed to process label:', err);
    }
  };

  const downloadZplFile = (zplContent, trackingNumber) => {
    const blob = new Blob([zplContent], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-${trackingNumber || 'shipping'}.zpl`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetToScan = () => {
    setState(STATES.SCAN_ITEM);
    setScannedItem('');
    setOrders([]);
    setWeight('');
    setBoxSize('small');
    setLabelData(null);
    setError('');
    setAutoWeight(null);
  };

  const handleStartOver = () => {
    resetToScan();
  };

  return (
    <div className="quick-shipment-page">
      <div className="quick-shipment-header">
        <h1>Quick Shipment</h1>
        <p className="subtitle">Fast fulfillment for single-item orders</p>
      </div>

      <div className="quick-shipment-content">
        <AnimatePresence mode="wait">
          {/* SCAN ITEM State */}
          {state === STATES.SCAN_ITEM && (
            <motion.div
              key="scan"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="state-container"
            >
              <div className="scan-section">
                <h2>Scan Item</h2>
                <p>Scan the item barcode or enter SKU/UPC</p>
                <ScanInput
                  ref={scanInputRef}
                  onScan={handleItemScan}
                  placeholder="Scan item barcode..."
                  disabled={false}
                />
              </div>
            </motion.div>
          )}

          {/* SEARCHING State */}
          {state === STATES.SEARCHING && (
            <motion.div
              key="searching"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="state-container"
            >
              <div className="loading-section">
                <div className="spinner" />
                <h2>Searching for orders...</h2>
                <p>Finding oldest pending order for {scannedItem}</p>
              </div>
            </motion.div>
          )}

          {/* ORDERS FOUND State */}
          {state === STATES.ORDERS_FOUND && orders.length > 0 && (
            <motion.div
              key="found"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="state-container"
            >
              <div className="orders-selection">
                <div className="success-badge">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2>{orders.length} Order{orders.length > 1 ? 's' : ''} Found!</h2>
                <p className="subtitle">Select orders to ship (all selected by default)</p>

                <div className="orders-list">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className={`order-card ${order.selected ? 'selected' : ''}`}
                      onClick={() => toggleOrderSelection(order.id)}
                    >
                      <div className="order-checkbox">
                        <input
                          type="checkbox"
                          checked={order.selected}
                          readOnly
                        />
                      </div>
                      <div className="order-info">
                        <div className="order-header">
                          <span className="order-number">#{order.orderNumber}</span>
                          <span className="order-date">{new Date(order.orderDate).toLocaleDateString()}</span>
                        </div>
                        <div className="order-customer">{order.customerName}</div>
                        <div className="order-location">
                          {order.shippingAddress.city}, {order.shippingAddress.state}
                        </div>
                        {order.items && order.items.length > 0 && (
                          <div className="order-item">
                            {order.items[0].description} (Qty: {order.items[0].quantity})
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="action-buttons">
                  <button className="cancel-btn" onClick={handleStartOver}>
                    Cancel
                  </button>
                  <button
                    className="continue-btn"
                    onClick={handleContinueToWeighing}
                    disabled={!orders.some(o => o.selected)}
                  >
                    Continue ({orders.filter(o => o.selected).length} selected)
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* WEIGHING State */}
          {state === STATES.WEIGHING && orders.length > 0 && (
            <motion.div
              key="weighing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="state-container"
            >
              <div className="weighing-section">
                <h2>Package Weight</h2>
                <p>Processing {orders.filter(o => o.selected).length} order(s)</p>

                <div className="weight-input-group">
                  {autoWeight ? (
                    <div className="auto-weight">
                      <div className="weight-badge">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                          <line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                      </div>
                      <div className="weight-display">
                        <span className="weight-value">{autoWeight}</span>
                        <span className="weight-unit">lbs</span>
                      </div>
                      <button
                        className="change-weight-btn"
                        onClick={() => {
                          setAutoWeight(null);
                          setWeight('');
                        }}
                      >
                        Manual Entry
                      </button>
                    </div>
                  ) : (
                    <div className="manual-weight">
                      <label>Weight (lbs)</label>
                      <input
                        ref={weightInputRef}
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder="Enter weight..."
                        className="weight-input"
                      />
                    </div>
                  )}
                </div>

                <div className="box-size-selector">
                  <label>Box Size</label>
                  <div className="box-options">
                    {BOX_SIZES.map((box) => (
                      <button
                        key={box.id}
                        className={`box-option ${boxSize === box.id ? 'selected' : ''}`}
                        onClick={() => setBoxSize(box.id)}
                      >
                        <span className="box-name">{box.name}</span>
                        <span className="box-dimensions">{box.dimensions}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="action-buttons">
                  <button className="cancel-btn" onClick={handleStartOver}>
                    Cancel
                  </button>
                  <button
                    className="process-btn"
                    onClick={handleProcessShipment}
                    disabled={!weight || parseFloat(weight) <= 0}
                  >
                    Generate Label
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* PROCESSING State */}
          {state === STATES.PROCESSING && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="state-container"
            >
              <div className="loading-section">
                <div className="spinner" />
                <h2>Processing Shipment...</h2>
                <p>Creating fulfillment and generating label</p>
              </div>
            </motion.div>
          )}

          {/* LABEL READY State */}
          {state === STATES.LABEL_READY && labelData && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="state-container"
            >
              <div className="label-ready-section">
                <div className="success-badge large">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2>Shipment{labelData.count > 1 ? 's' : ''} Complete!</h2>
                <p className="success-message">{labelData.count} label{labelData.count > 1 ? 's' : ''} generated</p>

                {labelData.results && labelData.results.length > 0 && (
                  <div className="shipments-list">
                    {labelData.results.map((result, index) => (
                      <div key={index} className="shipment-result">
                        <div className="tracking-info">
                          <div className="tracking-number">
                            <label>Tracking #{index + 1}</label>
                            <div className="tracking-display">{result.trackingNumber}</div>
                          </div>
                          <div className="fulfillment-number">
                            <label>Fulfillment #</label>
                            <div className="fulfillment-display">{result.fulfillmentNumber}</div>
                          </div>
                        </div>
                        {result.labelData && (
                          <button
                            className="print-label-btn-small"
                            onClick={() => printLabel(result.labelData, result.trackingNumber)}
                          >
                            Print/Download
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="action-buttons">
                  <button className="start-over-btn" onClick={resetToScan}>
                    Ship Another Item
                  </button>
                </div>

                <p className="auto-reset">Auto-resetting in 5 seconds...</p>
              </div>
            </motion.div>
          )}

          {/* ERROR State */}
          {state === STATES.ERROR && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="state-container"
            >
              <div className="error-section">
                <div className="error-badge">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h2>Error</h2>
                <p className="error-message">{error}</p>
                <div className="action-buttons">
                  <button className="retry-btn" onClick={resetToScan}>
                    Try Again
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default QuickShipmentPage;
